import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useCallback,
    ForwardedRef,
    ReactElement,
    cloneElement,
    isValidElement,
} from 'react';
import AutoSizer, { Size } from 'react-virtualized/dist/es/AutoSizer';
import BaseGrid, {
    GridCellProps,
    GridProps,
    SectionRenderedParams,
} from 'react-virtualized/dist/es/Grid';
import WindowScroller from 'react-virtualized/dist/es/WindowScroller';
import { withInvariant } from '../../utils/withInvariant';
import { Box } from './Box';
import { Stack } from './Stack';

interface VirtualizedGridRef {
    resize: (size: Size) => void;
}

interface VirtualizedGridProps extends Omit<GridProps, 'onSectionRendered'> {
    count: number;
    width: number;
    height: number;
    columns: number;
    scrollTop: number;
    perPage: number;
    isLoading: boolean;
    isEndReached: boolean;
    renderItem: (index: number) => ReactElement | null;
    onSectionRendered: (params: SectionRenderedParams, columnCount: number) => void;
}

const VirtualizedGrid = forwardRef(
    (props: VirtualizedGridProps, ref: ForwardedRef<VirtualizedGridRef>) => {
        const {
            width,
            height,
            perPage,
            scrollTop,
            isLoading,
            isEndReached,
            renderItem,
            columns,
            ...other
        } = props;
        const gridRef = useRef(null);
        const count = isLoading && !isEndReached ? props.count + perPage : props.count;
        const cellSize = useCallback(() => Math.floor(width / columns), [width, columns]);
        const onSectionRendered = (params: SectionRenderedParams) =>
            props.onSectionRendered(params, columns);

        useImperativeHandle(ref, () => ({
            resize() {
                // @ts-ignore
                gridRef.current?.recomputeGridSize();
            },
        }));

        return (
            <BaseGrid
                {...other}
                ref={gridRef}
                autoHeight
                width={width}
                height={height}
                rowHeight={cellSize}
                columnWidth={cellSize}
                scrollTop={scrollTop}
                columnCount={columns}
                rowCount={Math.ceil(count / columns)}
                onSectionRendered={onSectionRendered}
                estimatedRowSize={cellSize()}
                estimatedColumnSize={cellSize()}
                cellRenderer={({ key, style, rowIndex, columnIndex }: GridCellProps) => {
                    const element = renderItem(columnIndex + rowIndex * columns);

                    // @ts-ignore
                    return isValidElement(element) ? cloneElement(element, { key, style }) : null;
                }}
            />
        );
    },
);

interface InfiniteGridProps {
    defaultWidth?: number;
    defaultHeight?: number;
    threshold?: number;
    count: number;
    columns: number;
    perPage: number;
    isLoading: boolean;
    isEndReached: boolean;
    onLoadNext: VoidFunction;
    renderItem: VirtualizedGridProps['renderItem'];
}

export const InfiniteGrid = withInvariant(
    (props: InfiniteGridProps) => {
        const containerRef = useRef<VirtualizedGridRef | null>(null);
        const {
            count,
            columns,
            threshold = 3,
            isLoading,
            isEndReached,
            onLoadNext,
            defaultWidth,
            defaultHeight,
            ...other
        } = props;
        const onResize = useCallback(
            (size: Size) => containerRef.current?.resize(size),
            [containerRef.current],
        );

        const onSectionRendered = useCallback(
            (params: SectionRenderedParams) => {
                const isScrollToEnd = params.rowStopIndex + threshold >= Math.ceil(count / columns);

                if (isScrollToEnd && !isLoading && !isEndReached) {
                    onLoadNext();
                }
            },
            [onLoadNext, isLoading, isEndReached, threshold, count, columns],
        );

        return (
            <WindowScroller>
                {({ height, scrollTop, registerChild }) => (
                    <Box width="100%" ref={registerChild as any}>
                        <AutoSizer
                            disableHeight
                            defaultWidth={window?.innerWidth}
                            onResize={onResize}
                        >
                            {({ width }: Size) => (
                                <VirtualizedGrid
                                    {...other}
                                    ref={containerRef}
                                    count={count}
                                    columns={columns}
                                    width={width}
                                    height={height}
                                    scrollTop={scrollTop}
                                    isLoading={isLoading}
                                    isEndReached={isEndReached}
                                    onSectionRendered={onSectionRendered}
                                />
                            )}
                        </AutoSizer>
                    </Box>
                )}
            </WindowScroller>
        );
    },
    ({ count, columns, renderItem }: InfiniteGridProps) => (
        <Stack
            direction="row"
            flexWrap="wrap"
            maxWidth="100%"
            count={count}
            renderItem={(index) => {
                const element = renderItem(index);

                if (element) {
                    return cloneElement(element, {
                        key: index,
                        style: { width: `calc(100% / ${columns}` },
                    });
                }

                return <div style={{ width: `calc(100% / ${columns}` }} />;
            }}
        />
    ),
);
